import PropTypes from "prop-types";
import React, { memo, useCallback, useMemo } from "react";
import { GroupedVirtuoso } from "react-virtuoso";
import GroupItem from "./GroupItem";
import NotificationItem from "./NotificationItem";
import NotificationsLoadingFooter from "./NotificationsLoadingFooter";

// Module-scope constants — keeping these stable across renders prevents
// react-virtuoso from treating them as changed slots/props on every parent
// re-render (which would otherwise unmount the Footer).
const VIEWPORT_BUFFER = { top: 200, bottom: 200 };
const CONTAINER_STYLE = { height: "100%" };
const COMPONENTS = { Footer: NotificationsLoadingFooter };

// Renders the bell-tray notifications list with sticky group headers
// (Today / Yesterday / Older) using react-virtuoso's GroupedVirtuoso.
// See https://virtuoso.dev/grouped-virtuoso/.
const NotificationsGroup = memo(
  ({
    groupedNotifications,
    nextToken,
    isNextPageLoading,
    fetchNextPageOfNotifications,
    updatingNotificationIds,
    updatingAllNotifications,
    markAllNotificationsAsRead,
    toggleNotificationReadStatus,
  }) => {
    const { groups, groupCounts, items, firstUnreadGroupIndex } =
      groupedNotifications;

    const hasNextPage = nextToken != null;

    const handleEndReached = useCallback(() => {
      if (hasNextPage && !isNextPageLoading) {
        fetchNextPageOfNotifications();
      }
    }, [hasNextPage, isNextPageLoading, fetchNextPageOfNotifications]);

    const groupContent = useCallback(
      (index) => (
        <GroupItem
          name={groups[index]}
          showLink={index === firstUnreadGroupIndex}
          updatingAllNotifications={updatingAllNotifications}
          markAllNotificationsAsRead={markAllNotificationsAsRead}
        />
      ),
      [
        groups,
        firstUnreadGroupIndex,
        updatingAllNotifications,
        markAllNotificationsAsRead,
      ],
    );

    const itemContent = useCallback(
      (index) => {
        const notification = items[index];
        return (
          <NotificationItem
            notification={notification}
            isUpdating={updatingNotificationIds.has(notification?.ID)}
            toggleReadStatus={toggleNotificationReadStatus}
          />
        );
      },
      [items, updatingNotificationIds, toggleNotificationReadStatus],
    );

    const context = useMemo(
      () => ({ hasNextPage, isNextPageLoading }),
      [hasNextPage, isNextPageLoading],
    );

    return (
      <div
        className="notifications list-group"
        style={{ height: "calc(100vh - 130px)" }}
      >
        <GroupedVirtuoso
          groupCounts={groupCounts}
          groupContent={groupContent}
          itemContent={itemContent}
          endReached={hasNextPage ? handleEndReached : undefined}
          components={COMPONENTS}
          context={context}
          defaultItemHeight={100}
          increaseViewportBy={VIEWPORT_BUFFER}
          style={CONTAINER_STYLE}
        />
      </div>
    );
  },
);

NotificationsGroup.propTypes = {
  groupedNotifications: PropTypes.shape({
    groups: PropTypes.arrayOf(PropTypes.string).isRequired,
    groupCounts: PropTypes.arrayOf(PropTypes.number).isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({ ID: PropTypes.string }))
      .isRequired,
    firstUnreadGroupIndex: PropTypes.number,
  }).isRequired,
  nextToken: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isNextPageLoading: PropTypes.bool,
  fetchNextPageOfNotifications: PropTypes.func.isRequired,
  updatingNotificationIds: PropTypes.instanceOf(Set).isRequired,
  updatingAllNotifications: PropTypes.bool,
  markAllNotificationsAsRead: PropTypes.func.isRequired,
  toggleNotificationReadStatus: PropTypes.func.isRequired,
};

export default NotificationsGroup;
